import React, { useContext } from 'react';

import styled from '@emotion/styled';

import { ClickOutDropDown } from '../../../../components/DropDown/ClickOutDropDown';
import DeleteIcon from '../../../../components/Icons/DeleteIcon';
import DoubleCheckMark from '../../../../components/Icons/DoubleCheckMark';
import { MoreHorizontal } from '../../../../components/Icons/MoreHorizontal';
import Img from '../../../../components/Img/Img';
import { IInterviewData } from '../../../../models/jobs/singleJobApplicants.model';
import {
  DEFAULT_IMAGE_URL,
  formateDatetimeStrToNaijaTime,
} from '../../../../utils/utils';
import InterviewCardForm from './InterviewCardForm';
import useSetApplicationStatus from '../../../../hooks/useSetApplicationStatus';
import { LoadingOutlined } from '@ant-design/icons';
import { useApplicantsRefresh } from '../../../../context/ApplicantRefreshContext';
import toast from 'react-hot-toast';
import { ModalProvider } from '../../../../context/ModalContext';
import ModalTriggerContainer from '../../../../components/Modal/ModalTriggerContainer';
import ModalContainer from '../../../../components/Modal/ModalContainer';
import { useAppSelector } from '../../../../redux/store';
import useGetApplicantData from '../../../../hooks/jobs/useGetApplicantData';
import { ModalContext } from '../../../../context/ModalContext';
import { Modal } from '../../../../components/Modal/Modal';

const InterviewCardDropDown = ({
  onAccept,
  onReject,
  onStatusChange,
}: {
  onAccept: any;
  onReject: any;
  onStatusChange?: (newStatus: 'accepted' | 'rejected') => void;
}) => {
  const handleAccept = () => {
    onAccept();
    if (onStatusChange) {
      onStatusChange('accepted');
    }
  };

  const handleReject = () => {
    onReject();
    if (onStatusChange) {
      onStatusChange('rejected');
    }
  };

  return (
    <ClickOutDropDown
      tableFilter
      label={
        <IconArea>
          <MoreHorizontal />
        </IconArea>
      }
      borderRadius="12px"
      posRight="0px"
    >
      <DropDownContent>
        <DropDownCell onClick={handleAccept}>
          <DoubleCheckMark />
          Accept
        </DropDownCell>
        <DropDownCell onClick={handleReject}>
          <DeleteIcon />
          Reject
        </DropDownCell>
      </DropDownContent>
    </ClickOutDropDown>
  );
};

interface InterviewCardProps extends IInterviewData {
  setStatus: 'SCREENING' | 'INTERVIEW' | 'ACCEPTED' | 'REJECTED';
  nullify: boolean;
}

const InterviewCard: React.FC<InterviewCardProps> = ({
  id,
  createdAt,
  setStatus,
  interviewDate,
  duration,
  jobApplication,
  updatedAt,
  nullify,
}) => {
  const { loading, setApplicationStatus } = useSetApplicationStatus();

  const { triggerRefresh } = useApplicantsRefresh();

  const interviewMatch = useAppSelector((state) => state.interviewMatch);

  const modalContext = useContext(ModalContext);

  const {
    getApplicantData,
    applicantData,
    loading: dataload,
  } = useGetApplicantData('', modalContext?.showModal ?? false);

  const handleClick = () => {
    const matchedApplicant = interviewMatch.find(
      (item) => item.email === jobApplication.email
    );

    if (matchedApplicant) {
      getApplicantData(matchedApplicant.userId);
    }
  };

  return (
    <ModalProvider>
      <ModalTriggerContainer width="100%">
        {' '}
        <Container onClick={handleClick}>
          <Upper>
            <ImageHolder>
              <Img src={DEFAULT_IMAGE_URL} alt="image" />
            </ImageHolder>
            <div>
              <Name>
                {jobApplication.firstName} {jobApplication.lastName}
              </Name>
              <AppliedAt>
                interview scheduled for
                <span>{formateDatetimeStrToNaijaTime(interviewDate)}</span>
              </AppliedAt>
            </div>
            <MenuRegion>
              {loading ? (
                <LoadingOutlined />
              ) : (
                <div onClick={(e) => e.stopPropagation()}>
                  <InterviewCardDropDown
                    onAccept={() => {
                      if (nullify) {
                        toast.error(
                          'you have already accepted an Applicant for this job'
                        );
                        return;
                      } else {
                        setApplicationStatus(jobApplication.id, {
                          status: setStatus,
                        }).then(() => {
                          triggerRefresh();
                        });
                      }
                    }}
                    onReject={() => {
                      setApplicationStatus(jobApplication.id, {
                        status: 'REJECTED',
                      }).then(() => {
                        triggerRefresh();
                      });
                    }}
                  />
                </div>
              )}
            </MenuRegion>
          </Upper>
          <Bottom>
            {/* {status === 'ACCEPTED' ? (
          <Accept>accepted</Accept>
        ) : (
          <InterviewCardForm />
        )} */}
            <InterviewCardForm />
          </Bottom>
        </Container>
      </ModalTriggerContainer>
      <Modal>
        <ModalContainer marginTop="15rem" padding="2rem">
          {dataload ? (
            <LoadingOutlined />
          ) : (
            <>
              {' '}
              <P>
                email: <span>{applicantData?.email}</span>
              </P>
              <P>
                phone: <span>{applicantData?.phone || 'N/A'}</span>
              </P>
            </>
          )}
        </ModalContainer>
      </Modal>
    </ModalProvider>
  );
};

export default InterviewCard;

const Container = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.greyGrey4};
  box-shadow: 0px 20px 26px 0px rgba(186, 182, 182, 0.16);
  border-radius: 6px;
`;

const Upper = styled.div`
  display: flex;
  gap: 6px;
  padding: 13px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.greyGrey2};
`;

const ImageHolder = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 16px;
`;

const IconArea = styled.div`
  cursor: pointer;
  /* border: 2px solid red; */
  height: 16px;
  display: grid;
  place-content: center;
`;

const Bottom = styled.div`
  padding: 13px 16px;
  width: 100%;
  display: flex;
  gap: 16px;
`;

const Name = styled.div`
  display: flex;
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.blackBlack2};
`;

const AppliedAt = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.greyGrey2};

  & > span {
    color: ${({ theme }) => theme.palette.mainBlue};
  }
`;

const DropDownContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* pad */
`;

const DropDownCell = styled.div`
  display: grid;
  grid-template-columns: 16px 132px;
  gap: 18px;
  padding: 8px;
  align-items: center;
  width: 132px;

  &:first-of-type {
    border-bottom: 1px solid #e9eeef;
  }

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.palette.greyGrey4};
  }

  &:last-of-type {
    color: ${({ theme }) => theme.palette.stateColorRed};
  }
`;

const MenuRegion = styled.div`
  margin-left: auto;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 10px;
`;

const P = styled.div`
  font-size: 18px;
  font-weight: 600;

  & > span {
    font-size: 14px;
    font-weight: 400;
  }
`;
